import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import { loadMetricCategories } from '@api/filters';
import * as styles from './MetricCategoryFilter.module.css';

/**
 * MetricCategoryFilter Component
 * Uses EntityPanelSelector in TWO_COLUMNS mode (no filter dropdown)
 */
function MetricCategoryFilter({
  selectedMetricCategoryIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [availableMetricCategories, setAvailableMetricCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load metric categories
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const metricCategories = await loadMetricCategories();
        setAvailableMetricCategories(metricCategories);
      } catch (error) {
        console.error('Error loading metric categories:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Labels for EntityPanelSelector
  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: t('reports.filters.noMetricCategoriesAvailable'),
    noItemsFound: t('reports.filters.noMetricCategoriesFound'),
    noItemsSelected: t('reports.filters.noEntitiesSelected'),
  }), [t]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <EntityPanelSelector
      mode={LAYOUT_MODES.TWO_COLUMNS}
      availableItems={availableMetricCategories}
      selectedItemIds={selectedMetricCategoryIds}
      onSelectionChange={onSelectionChange}
      labels={labels}
      valueField="id"
    />
  );
}

MetricCategoryFilter.propTypes = {
  selectedMetricCategoryIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export { MetricCategoryFilter };
export default MetricCategoryFilter;
